<script setup lang="ts">
import { $patchStyleText } from "@lexical/selection";
import { mergeRegister } from "@lexical/utils";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_ENTER_COMMAND,
  type RangeSelection,
  TextNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import { TEXT_FORMATS } from "../const";
import { $isTypeaheadNode } from "../nodes/TypeaheadNode";

const editor = useLexicalComposer();

const stripAllTextFormats = (selectionOrTextNode: RangeSelection | TextNode) => {
  TEXT_FORMATS.forEach((format) => {
    if (selectionOrTextNode.hasFormat(format)) {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    }
  });
};

const stripAllTextStyles = (selection: RangeSelection) => {
  $patchStyleText(selection, { color: null, "background-color": null });
};

const updateEditorStyles = () => {
  editor.update(() => {
    const newSelection = $getSelection();
    if ($isRangeSelection(newSelection)) {
      stripAllTextStyles(newSelection);
    }
  });
};

const handleEnter = () => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return;
  }

  stripAllTextFormats(selection);
  updateEditorStyles();
};

const handleBackspace = () => {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return;
  }

  if (selection.anchor.getNode().getTextContent().length > 1) {
    return;
  }

  stripAllTextFormats(selection);
  updateEditorStyles();
};

const handleArrowRight = () => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.getTextContent() !== "") {
    return;
  }

  const node = selection.anchor.getNode();

  if (!($isTextNode(node) || $isTypeaheadNode(node)) || node.getTextContentSize() === 0 || node.getFormat() === 0) {
    return;
  }

  const textContent = node.getTextContent();
  const indexOfCursor = selection.anchor.offset;

  // Not at the end of the line
  if (indexOfCursor !== textContent.length) {
    return;
  }
  const nextSibling = node.getNextSibling();

  if (nextSibling) {
    node.selectNext(0, 0);
    return;
  }

  const textNode = $createTextNode(" ");
  selection.insertNodes([textNode]);
  stripAllTextFormats(selection);
  stripAllTextStyles(selection);
};

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => {
        handleEnter();
        return false;
      },
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => {
        handleBackspace();
        return false;
      },
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      () => {
        handleArrowRight();
        return false;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
